import { useCallback, useEffect, useMemo, useState } from 'react';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  AgentConnectivityState,
  CreateModelProviderSecretRequest,
  ModelProviderSecret,
  ModelProviderSecretType,
  PersistableModelProvider,
  Team,
  WsServerMessage,
} from '@reflex/shared';
// `./http` lives next to these hooks because the plugin client SDK owns HTTP
// transport (auth/org headers, 401 handling). It does not belong in
// `@reflex/ui` (design system) or `@reflex/shared` (types-only, no runtime).
// Slug routing is handled separately in `PluginClientContext`.
import { request } from './http';
// Generated, typed agents client (orval). The hand-written hooks below keep
// their public signatures and project query keys but route the actual network
// call through these generated fetchers, so request/response types track the
// server contract. See `docs/internal/architecture/api-contract.md`.
import {
  archiveAgent as apiArchiveAgent,
  createAgent as apiCreateAgent,
  getAgent as apiGetAgent,
  getAgentStream as apiGetAgentStream,
  killAgent as apiKillAgent,
  listAgents as apiListAgents,
  stopAgent as apiStopAgent,
  unarchiveAgent as apiUnarchiveAgent,
} from './generated/agents';
import type { CreateAgentBody } from './generated/model/createAgentBody';
import {
  getBlueprint as apiGetBlueprint,
  listBlueprints as apiListBlueprints,
} from './generated/blueprints';
import { listSnapshots as apiListSnapshots } from './generated/snapshots';
import { getUser as apiGetUser, listUsers as apiListUsers } from './generated/users';
import { listAvailableSecrets as apiListAvailableSecrets } from './generated/secrets';
import {
  getOrganization as apiGetOrganization,
  listOrgTeams as apiListOrgTeams,
} from './generated/organizations';
import { getConfig as apiGetConfig } from './generated/config';
import {
  createMyModelProviderSecret as apiCreateMyModelProviderSecret,
  listAccessibleModelProviderSecrets as apiListAccessibleModelProviderSecrets,
} from './generated/me';
import { createTeamModelProviderSecret as apiCreateTeamModelProviderSecret } from './generated/organizations';
import {
  usePluginAuth,
  useActiveOrgSlug,
  useOrgKey,
  useOrgRoutes,
  usePluginRegistry,
  usePluginSocket,
} from './PluginClientContext';
import { queryKeys } from './query-keys';
import type {
  ResolvedFlowTriggerRenderer,
  ResolvedPageTab,
  ResolvedToolCallRenderer,
} from './interfaces';
import { DEVBOX_STATUS_POLL_MS, devboxStatusPollInterval } from './devbox-status';
export { usePluginAuth as useAuth, useActiveOrgSlug, useOrgKey, useOrgRoutes };
export {
  devboxStatusPollInterval,
  isDevboxStatusPollEligible,
  isDevboxGoneAgentStatus,
  isResumableDevboxStatus,
} from './devbox-status';
export {
  useTunnelProbe,
  probeTunnelEndpoint,
  classifyTunnelProbeStatus,
  TUNNEL_PROBE_RETRY_INTERVAL_MS,
  type TunnelProbeController,
  type TunnelProbeState,
  type TunnelProbeTarget,
} from './tunnel-probe';
export type QueuedMessage = {
  id: string;
  text: string;
  message?: string;
  content?: unknown;
  createdAt?: number;
  [key: string]: unknown;
};
export function useUser(userId?: string | null) {
  return useQuery({
    queryKey: queryKeys.user(userId ?? ''),
    queryFn: () => apiGetUser(userId ?? ''),
    enabled: Boolean(userId),
  });
}
// `scope: 'org'` forces server-side org scoping so platform admins viewing
// the active org see only that org's members in member-picker UIs, not every
// user across every organization.
export function useUsers() {
  const orgKey = useOrgKey();
  return useQuery({
    queryKey: queryKeys.users(orgKey),
    queryFn: () => apiListUsers({ scope: 'org' }),
  });
}
// Teams come straight from the active membership — they're synchronous
// auth-context data, not a fetched resource. Returns `{ data }` for parity with
// the fetch-backed client hooks so call sites destructure uniformly.
export function useMyTeams(): { data: Team[] } {
  const auth = usePluginAuth();
  return useMemo(() => ({ data: auth.activeMembership?.teams ?? [] }), [auth.activeMembership]);
}
/**
 * Every team in the active org (`teams:read`), for owner filters and team-name
 * resolution beyond the caller's own memberships. Degrades to the caller's
 * membership teams when the permission is missing or the fetch fails, so
 * consumers always get a usable (if narrower) list.
 */
export function useOrgTeams(): { data: Team[]; isLoading: boolean } {
  const auth = usePluginAuth();
  const orgKey = useOrgKey();
  const { data: myTeams } = useMyTeams();
  const orgId = auth.activeOrganizationId;
  const enabled = Boolean(orgId) && auth.can('teams:read');
  const query = useQuery({
    queryKey: queryKeys.orgTeams(orgKey),
    queryFn: () => apiListOrgTeams(orgId ?? ''),
    enabled,
    retry: false,
    staleTime: 30_000,
  });
  return useMemo(
    () => ({
      data: enabled && query.data ? query.data.teams : myTeams,
      isLoading: enabled && query.isLoading,
    }),
    [enabled, query.data, query.isLoading, myTeams],
  );
}
export function useOrganization(id?: string | null) {
  return useQuery({
    queryKey: queryKeys.organization(id ?? ''),
    queryFn: () => apiGetOrganization(id ?? ''),
    enabled: Boolean(id),
  });
}
export function useAgents(_userId?: string | null) {
  const orgKey = useOrgKey();
  const result = useInfiniteQuery({
    queryKey: queryKeys.agents(orgKey, { userId: _userId }),
    queryFn: ({ pageParam }) =>
      apiListAgents({
        ...(_userId ? { userId: _userId } : {}),
        ...(pageParam ? { cursor: pageParam } : {}),
      }),
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage) => lastPage.nextCursor,
  });
  const agents = useMemo(
    () => result.data?.pages.flatMap((page) => page?.agents ?? []) ?? [],
    [result.data],
  );

  return { ...result, data: agents };
}
export function useAgent(id?: string | null) {
  const orgKey = useOrgKey();
  return useQuery({
    queryKey: queryKeys.agent(orgKey, id ?? ''),
    queryFn: () => apiGetAgent(id ?? ''),
    enabled: Boolean(id),
  });
}
export function useAgentStream(id?: string | null) {
  return useQuery({
    // The full-history slot, not `queryKeys.stream`: for paged agents the
    // host app's `['stream', id]` entry holds only the loaded window, and
    // this hook's full array must not overwrite it (or be overwritten).
    queryKey: queryKeys.streamFull(id ?? ''),
    // Without query parameters the server returns the full event array; the
    // envelope arm of the response union only ever answers paged requests,
    // so unwrapping it here is type-completeness, not a path this hook takes.
    queryFn: async () => {
      const data = await apiGetAgentStream(id ?? '');
      return Array.isArray(data) ? data : data.events;
    },
    enabled: Boolean(id),
  });
}

/**
 * Mutation to start a new agent. On success:
 * - Sets the new agent in the individual agent cache
 * - Invalidates all agents lists under the active org
 */
export function useStartAgent() {
  const qc = useQueryClient();
  const orgKey = useOrgKey();
  return useMutation({
    mutationFn: (body: CreateAgentBody) => apiCreateAgent(body),
    onSuccess: (agent) => {
      qc.setQueryData(queryKeys.agent(orgKey, agent.id), agent);
      void qc.invalidateQueries({ queryKey: ['agents', orgKey] });
    },
  });
}

/**
 * Mutation to stop an agent. Invalidates org-scoped agents list.
 */
export function useStopAgent() {
  const qc = useQueryClient();
  const orgKey = useOrgKey();
  return useMutation({
    mutationFn: (id: string) => apiStopAgent(id),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['agents', orgKey] }),
  });
}

/**
 * Mutation to archive an agent. Invalidates org-scoped agents list.
 */
export function useArchiveAgent() {
  const qc = useQueryClient();
  const orgKey = useOrgKey();
  return useMutation({
    mutationFn: (id: string) => apiArchiveAgent(id),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['agents', orgKey] }),
  });
}

/**
 * Mutation to permanently kill an agent. Invalidates org-scoped agents list.
 */
export function useKillAgent() {
  const qc = useQueryClient();
  const orgKey = useOrgKey();
  return useMutation({
    mutationFn: (id: string) => apiKillAgent(id),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['agents', orgKey] }),
  });
}

/**
 * Mutation to unarchive an agent. Invalidates org-scoped agents list.
 */
export function useUnarchiveAgent() {
  const qc = useQueryClient();
  const orgKey = useOrgKey();
  return useMutation({
    mutationFn: (id: string) => apiUnarchiveAgent(id),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['agents', orgKey] }),
  });
}
export interface AgentConnectivity {
  state: AgentConnectivityState;
  since?: number;
  reason?: string;
}

interface AgentConnectivityInternalState {
  agentId: string | undefined;
  conn: AgentConnectivity;
}

const DEFAULT_AGENT_CONNECTIVITY: AgentConnectivity = { state: 'live' };

export function useAgentConnectivity(agentId?: string): AgentConnectivity {
  const socket = usePluginSocket();
  const [state, setState] = useState<AgentConnectivityInternalState>({
    agentId,
    conn: DEFAULT_AGENT_CONNECTIVITY,
  });

  const handler = useCallback(
    (msg: WsServerMessage) => {
      if (!agentId) return;
      if (msg.type !== 'agent:connectivity') return;
      if (msg.agentId !== agentId) return;
      setState({
        agentId,
        conn: { state: msg.state, since: msg.since, reason: msg.reason },
      });
    },
    [agentId],
  );

  useEffect(() => socket.onMessage(handler), [handler, socket]);

  return state.agentId === agentId ? state.conn : DEFAULT_AGENT_CONNECTIVITY;
}

/**
 * Fetch the org's blueprint list. Uses org-scoped cache key so cross-org
 * data never collides when the active org is switched mid-session.
 */
export function useBlueprints() {
  const orgKey = useOrgKey();
  return useQuery({
    queryKey: queryKeys.blueprints(orgKey),
    queryFn: () => apiListBlueprints(),
  });
}
export function useBlueprint(id?: string | null) {
  const orgKey = useOrgKey();
  return useQuery({
    queryKey: queryKeys.blueprint(orgKey, id ?? ''),
    queryFn: () => apiGetBlueprint(id ?? ''),
    enabled: Boolean(id),
  });
}

/**
 * Fetch the org's devbox snapshot list. The server returns an empty list for
 * orgs without the `devbox-snapshots` capability, so callers can pass the
 * result straight to a picker without a separate capability gate. Org-scoped
 * cache key, like {@link useBlueprints}.
 */
export function useSnapshots() {
  const orgKey = useOrgKey();
  return useQuery({
    queryKey: queryKeys.snapshots(orgKey),
    queryFn: () => apiListSnapshots(),
  });
}
export function useAvailableBlueprints<T>(_blueprints: T, _repoUrl?: string | null): T;
export function useAvailableBlueprints(): ReturnType<typeof useBlueprints>;
export function useAvailableBlueprints<T>(_blueprints?: T, _repoUrl?: string | null) {
  return _blueprints ?? useBlueprints();
}
export function findRepoBlueprint<T>(
  blueprints: T[] | undefined,
  repoUrl?: string | null,
): T | null {
  if (!repoUrl || !blueprints) return null;
  const normalized = repoUrl.replace(/^https?:\/\/github\.com\//, '');
  return (
    blueprints.find((bp) => {
      const bpAny = bp as { status?: string; metadata?: { repo?: string } };
      if (bpAny.status !== 'build_complete') return false;
      const r = bpAny.metadata?.repo;
      if (!r) return false;
      return r === repoUrl || r === normalized;
    }) ?? null
  );
}
export function usePlugins() {
  const registry = usePluginRegistry();
  return {
    routes: [],
    rootLevelRoutes: [],
    publicRoutes: [],
    chromelessAuthedRoutes: [],
    chromelessPublicRoutes: [],
    agentTabs: [],
    agentActions: [],
    agentHeaderBadges: [],
    agentTypes: registry?.agentTypes ?? [],
    eventLabels: registry?.eventLabels ?? new Map<string, unknown>(),
    resourcePreviews: registry?.resourcePreviews ?? new Map<string, unknown>(),
    flowTriggerRenderers:
      registry?.flowTriggerRenderers ?? new Map<string, ResolvedFlowTriggerRenderer>(),
    toolCallRenderers: registry?.toolCallRenderers ?? new Map<string, ResolvedToolCallRenderer>(),
    pageTabs: registry?.pageTabs ?? new Map<string, ResolvedPageTab[]>(),
    attachments: new Map<string, unknown>(),
    plugins: [],
    isLoading: false,
  };
}
/** Stable identity so `usePageTabs` doesn't churn dependent memos. */
const EMPTY_PAGE_TABS: ResolvedPageTab[] = [];

/**
 * Tabs other plugins contributed to the tab strip named by `slot`, already
 * ordered. A page that owns a slot renders its own tabs and appends these,
 * which is how a plugin adds a tab to a page it must not import from: the
 * Automations tab on the Replays page comes from `plugin-flows` while the
 * page itself is owned by `plugin-replay`. Returns an empty list when no
 * plugin contributes to the slot (e.g. the contributing plugin is
 * disabled), so the hosting page must stay usable with its own tabs alone.
 */
export function usePageTabs(slot: string): ResolvedPageTab[] {
  const { pageTabs } = usePlugins();
  return pageTabs.get(slot) ?? EMPTY_PAGE_TABS;
}

export function useEnabledModels() {
  return useQuery({
    queryKey: queryKeys.chatModels,
    queryFn: () => request<Array<{ id: string; enabled: boolean }>>('/chat/models'),
  });
}
export function useTunnelUrl(tunnelKey?: string | null, port?: number) {
  const { data } = useConfig();
  if (!tunnelKey || !port || !data?.tunnelDomain) return null;
  return `https://${port}-${tunnelKey}.${data.tunnelDomain}`;
}
export function usePlatformUrl() {
  const { data } = useConfig();
  return data?.platformUrl ?? null;
}
/**
 * Web origin for the deployment's GitHub host. Falls back to github.com while
 * the config query is in flight or on a non-enterprise deployment. Build
 * repository links as `${useGitHubWebBaseUrl()}/${owner/repo}` so GitHub
 * Enterprise Server users land on their host instead of github.com.
 */
export function useGitHubWebBaseUrl() {
  const { data } = useConfig();
  return data?.githubWebBaseUrl ?? 'https://github.com';
}
export function useConfig() {
  return useQuery({
    queryKey: ['config'],
    queryFn: () => apiGetConfig(),
  });
}
/**
 * Poll the live Runloop devbox status for an agent while a sandbox-backed tab
 * is open. Drives {@link SuspendedDevboxGuard} from the real devbox state
 * rather than the Axon event stream, which doesn't reliably emit a
 * `devbox.running` event when an HTTP request wakes a suspended box.
 *
 * While the box is suspended or coming back up the poll runs at a faster
 * cadence so the guard clears promptly once an HTTP probe or explicit resume
 * wakes it, then relaxes back to the base cadence once it reports `running`.
 *
 * `status` is the raw Runloop status string (`running`, `suspended`,
 * `resuming`, …), `null` when the agent has no devbox yet, or `'unknown'` when
 * the lookup couldn't resolve. Polling pauses while the tab is backgrounded.
 */
export function useDevboxStatus(
  agentId: string | null | undefined,
  options?: { enabled?: boolean; intervalMs?: number },
) {
  const enabled = (options?.enabled ?? true) && Boolean(agentId);
  const baseMs = options?.intervalMs ?? DEVBOX_STATUS_POLL_MS;
  return useQuery({
    queryKey: queryKeys.devboxStatus(agentId ?? ''),
    queryFn: () =>
      request<{ status: string | null }>(
        `/agents/${encodeURIComponent(agentId ?? '')}/devbox-status`,
      ),
    enabled,
    refetchInterval: (query) =>
      enabled ? devboxStatusPollInterval(query.state.data?.status, baseMs) : false,
    refetchIntervalInBackground: false,
    staleTime: 0,
  });
}
/**
 * Explicitly resume a suspended devbox. Exec-based surfaces (shell,
 * filesystem) reach the box over the control plane, which does NOT wake a
 * suspended box the way the browser's HTTP tunnel probe does — they must call
 * this before retrying. The server endpoint awaits `running` and is a no-op
 * when the box isn't suspended, so it's always safe to call as the guard's
 * resume handler. On success the cached devbox status is primed to `running`
 * so the suspend guard clears immediately.
 */
export function useResumeDevbox(agentId: string | null | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () =>
      request<{ status: string | null }>(`/agents/${encodeURIComponent(agentId ?? '')}/resume`, {
        method: 'POST',
      }),
    onSuccess: (data) => {
      queryClient.setQueryData(queryKeys.devboxStatus(agentId ?? ''), data);
    },
  });
}
export const orgPluginsQueryKey = (orgId: string | null | undefined) =>
  ['org-plugins', orgId ?? 'none'] as const;
export function useAgentTypeRegistry() {
  const { agentTypes } = usePlugins();
  return {
    agentTypes,
    getAgentType: (type?: string) =>
      agentTypes.find((agentType) => agentType.type === type) ?? null,
    getAll: () => agentTypes,
  };
}
export {
  useAgentModelSupportMatrix,
  useAgentProviderKeyTarget,
  resolveAgentProviderKeyTarget,
  type AgentProviderKeyTarget,
  type AgentModelSupportMatrix,
  type AgentModelSupportMatrixEntry,
} from './agent-key-provider';

/**
 * Owner of the resource a secret is being selected for. A prefixed id
 * (`usr_*` / `tem_*` / `org_*` / `prj_*`) the server narrows the query
 * against. Pass the calling user's id for the "Mine" picker case, the
 * active org id for "Organization", a team id for team-scoped reads.
 */
export interface AvailableSecretsOwner {
  ownerId: string;
}

function ownerSignature(owner?: AvailableSecretsOwner): string {
  return owner?.ownerId ?? '';
}

export function useAvailableSecrets(nameQuery?: string, owner?: AvailableSecretsOwner) {
  const orgKey = useOrgKey();
  const normalized = (nameQuery ?? '').trim();
  const signature = ownerSignature(owner);
  const { data, isLoading, isError } = useQuery({
    queryKey: queryKeys.secretsAvailable(orgKey, normalized, signature),
    queryFn: () =>
      apiListAvailableSecrets({
        ...(normalized ? { name: normalized } : {}),
        ...(owner ? { ownerId: owner.ownerId } : {}),
      }),
    staleTime: 30_000,
  });
  return { secrets: data?.secrets ?? [], isLoading, isError };
}
/**
 * Every model-provider secret (`mps_*`) the caller can use to launch an
 * agent or run a chat, folded across user + team + org tiers and sorted
 * by precedence (user → team → org). `types` narrows the set — the
 * launch picker passes `['apiKey', 'subscription']`, the flow-chat
 * picker passes `['apiKey']`. Backed by `GET
 * /me/model-provider-secrets/accessible`, which needs no
 * `secrets:manage` / `teams:manage` because a member who can launch
 * must be able to see the keys available to them.
 */
/**
 * Owner of the resource a key is being selected for. A single prefixed
 * id — kind (`user` / `team` / `organization` / `project`) is derived
 * from the prefix server-side, and the result is narrowed to the scopes
 * that owner may bind a key from (`user` -> user+team+org, `team` ->
 * team+org, `organization` -> org).
 */
export interface AccessibleSecretsOwner {
  ownerId: string;
}

export function useAccessibleModelProviderSecrets(options?: {
  types?: readonly ModelProviderSecretType[];
  owner?: AccessibleSecretsOwner;
  enabled?: boolean;
}) {
  const orgKey = useOrgKey();
  const types = options?.types ?? [];
  const owner = options?.owner;
  const ownerKey = owner?.ownerId;
  const { data, isLoading, isFetching, isError, refetch } = useQuery({
    queryKey: queryKeys.accessibleModelProviderSecrets(orgKey, types, ownerKey),
    queryFn: () =>
      apiListAccessibleModelProviderSecrets({
        ...(types.length > 0 ? { types: [...types].join(',') } : {}),
        ...(owner ? { ownerId: owner.ownerId } : {}),
      }),
    enabled: options?.enabled ?? true,
    staleTime: 30_000,
  });
  // `isFetching` also covers a background refetch over existing data, which
  // `isLoading` (react-query's `isPending`) does not. Consumers that reconcile
  // a selection against this list need it: a key added elsewhere is invalidated
  // but not yet fetched, so the in-hand list is knowably incomplete.
  return { secrets: data?.secrets ?? [], isLoading, isFetching, isError, refetch };
}

/**
 * Scope a freshly-created model-provider key lands under. The inline
 * "Add a key" affordance only offers `user` (personal) and `team`
 * because those write directly to the `mps_*` create endpoints. The org
 * tier intentionally goes through its own settings page so the legacy
 * gateway-sync + wrapper-mirroring server hooks fire, so it is not
 * offered here.
 */
export type CreateModelProviderSecretScope = 'user' | 'team';

export interface CreateModelProviderSecretInput {
  scope: CreateModelProviderSecretScope;
  /** Required when `scope === 'team'`. */
  teamId?: string;
  provider: PersistableModelProvider;
  name: string;
  value: string;
  /** Optional custom base URL for providers that support it (Anthropic, OpenAI). */
  baseUrl?: string | null;
}

/**
 * Create a model-provider API key (`mps_*`) without leaving the current
 * surface. Routes to the personal (`POST /me/model-provider-secrets`) or
 * team (`POST /organizations/:org/teams/:team/model-provider-secrets`)
 * endpoint based on the chosen scope, then invalidates the accessible +
 * per-scope lists so every picker that reads them re-fetches and the new
 * key appears. Mirrors the read hook {@link useAccessibleModelProviderSecrets}.
 */
export function useCreateModelProviderSecret() {
  const orgKey = useOrgKey();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateModelProviderSecretInput): Promise<ModelProviderSecret> => {
      const body: CreateModelProviderSecretRequest = {
        provider: input.provider,
        type: 'apiKey',
        name: input.name,
        value: input.value,
        baseUrl: input.baseUrl,
      };
      if (input.scope === 'team') {
        if (!input.teamId) {
          return Promise.reject(new Error('A team must be selected for a team-scoped key.'));
        }
        return apiCreateTeamModelProviderSecret(orgKey, input.teamId, body);
      }
      return apiCreateMyModelProviderSecret(body);
    },
    onSuccess: (_secret, input) => {
      // Partial-match invalidation: the accessible key carries a trailing
      // `types` segment, so target the prefix to refresh every variant
      // (chat's `apiKey` list, launch's `apiKey,subscription` list, …).
      void qc.invalidateQueries({
        queryKey: ['user', 'model-provider-secrets', 'accessible', orgKey],
      });
      void qc.invalidateQueries({ queryKey: queryKeys.myModelProviderSecrets(orgKey) });
      if (input.scope === 'team' && input.teamId) {
        void qc.invalidateQueries({
          queryKey: queryKeys.teamModelProviderSecrets(orgKey, input.teamId),
        });
      }
    },
  });
}

export function useBaseBlueprintBuilding() {
  return false;
}
export function useLaunchFormPersonas() {
  return [];
}
